import { useEffect, useState } from "react";
import "./Pagination.scss";
import { PaginationProps } from "/Models/Generated/PaginationProps";
import { Button, Pagina } from "/App.Components";

interface PaginationExtendedProps extends PaginationProps {
  setCurrentPage: (pageNum: number) => void;
  nextHrefForCrawlers?: string;
  previousHrefForCrawlers?: string;
}

const Pagination = ({
  currentPage,
  setCurrentPage,
  totalPages,
  previousButtonText,
  nextButtonText,
  ariaLabel,
  mobileText,
  selectAriaLabel,
  goToPageAriaLabel,
  nextHrefForCrawlers,
  previousHrefForCrawlers,
}: PaginationExtendedProps) => {
  const maxElements = 10;
  const hasTooManyElements = totalPages > maxElements;
  const [display, setDisplay] = useState(
    [...Array(10).keys()].slice(0, totalPages)
  );

  const [currentChunk, setCurrentChunk] = useState(
    hasTooManyElements && currentPage > 5
      ? display.map((x) => x + Math.max(currentPage - 5, 1))
      : display.map((x) => x + 1)
  );

  const goTo = (pageNum: number) => {
    setCurrentPage(pageNum);

    //if we have enough numbers on each side of current page we center it
    if (hasTooManyElements) {
      if (pageNum > 5 && pageNum < totalPages - 5) {
        setCurrentChunk(() => display.map((x) => x + pageNum - 5));
      } else if (pageNum <= 5) {
        setCurrentChunk(() =>
          [...Array(Math.min(11, totalPages)).keys()].slice(1)
        );
      } else {
        setCurrentChunk(() => display.map((x) => x + totalPages - 9));
      }
    }
  };

  useEffect(() => {
    setDisplay([...Array(10).keys()].slice(0, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentChunk(
      hasTooManyElements && currentPage > 5
        ? display.map((x) => x + Math.max(currentPage - 5, 1))
        : display.map((x) => x + 1)
    );
  }, [display]);

  return (
    <nav className="pagination-norad" aria-label={ariaLabel}>
      {nextHrefForCrawlers && (
        <a className="pagination-norad__hidden-link" href={nextHrefForCrawlers}>
          _NEXT_
        </a>
      )}
      {previousHrefForCrawlers && (
        <a
          className="pagination-norad__hidden-link"
          href={previousHrefForCrawlers}
        >
          _PREV_
        </a>
      )}
      {currentPage > 1 && (
        <Button
          label={previousButtonText ?? ""}
          style={currentPage > 1 ? "primary" : "disabled"}
          size="s"
          onClick={currentPage > 1 ? () => goTo(currentPage - 1) : () => {}}
          disabled={currentPage <= 1}
        />
      )}
      <div className="pagination-norad--desktop" data-testid="pagination">
        {currentChunk.map((x) => (
          <Pagina
            goToPageAriaLabel={goToPageAriaLabel}
            key={x}
            navigationHandler={goTo}
            number={
              x === currentChunk.at(-1) && hasTooManyElements
                ? totalPages
                : x === currentChunk[0] && hasTooManyElements
                  ? 1
                  : x
            }
            isEllipsis={
              hasTooManyElements &&
              ((x === currentChunk[currentChunk.length - 2] &&
                !(currentChunk.at(-1) === totalPages)) ||
                (x === currentChunk[1] && !(currentChunk[0] === 1)))
            }
            isHighlighted={x === currentPage}
          />
        ))}
      </div>
      <select
        className="pagination-norad--mobile"
        value={currentPage}
        onChange={(e) => goTo(Number(e.target.value))}
        aria-label={selectAriaLabel}
      >
        {Array.from({ length: totalPages }, (_, index) => (
          <option key={index + 1} value={index + 1}>
            {`${index + 1} ${mobileText} ${totalPages}`}
          </option>
        ))}
      </select>
      {currentPage < totalPages && (
        <Button
          label={nextButtonText ?? ""}
          style={currentPage < totalPages ? "primary" : "disabled"}
          size="s"
          onClick={
            currentPage < totalPages ? () => goTo(currentPage + 1) : () => {}
          }
          disabled={currentPage >= totalPages}
        />
      )}
    </nav>
  );
};

export default Pagination;
